import { z } from 'zod'

/** Which side of the two-pane browser a path belongs to (PROMPT.md §5.7). */
export const fileSideSchema = z.enum(['local', 'remote'])
export type FileSide = z.infer<typeof fileSideSchema>

export const fileKindSchema = z.enum(['file', 'directory', 'symlink', 'other'])
export type FileKind = z.infer<typeof fileKindSchema>

export const fileEntrySchema = z.object({
  name: z.string(),
  /** Absolute path on its side. */
  path: z.string(),
  kind: fileKindSchema,
  size: z.number().nonnegative(),
  modifiedAt: z.iso.datetime().nullable(),
  /** POSIX permission bits, without the file type. */
  mode: z.number().int().nonnegative(),
  /** `rwxr-xr-x`, for reading at a glance. */
  modeText: z.string(),
  uid: z.number().int().nonnegative().nullable(),
  gid: z.number().int().nonnegative().nullable(),
  /** Dotfiles are marked, not dropped — the pane decides whether to show them. */
  hidden: z.boolean(),
  /** Where a symlink points, when it could be read. */
  linkTarget: z.string().nullable(),
})
export type FileEntry = z.infer<typeof fileEntrySchema>

export const directoryListingSchema = z.object({
  side: fileSideSchema,
  /** The resolved absolute path; `~` and `.` are resolved by the backend. */
  path: z.string(),
  /** Null at the filesystem root. */
  parentPath: z.string().nullable(),
  entries: z.array(fileEntrySchema),
})
export type DirectoryListing = z.infer<typeof directoryListingSchema>

/**
 * Every file request names a side, and the remote side names a server. Carrying both in one
 * shape lets a single route serve both panes instead of two nearly identical ones.
 */
const sideTarget = {
  side: fileSideSchema,
  /** Required for `side: 'remote'`, ignored for the local pane. */
  serverId: z.string().min(1).optional(),
}

const remoteWithoutServer = (value: { side: FileSide; serverId?: string | undefined }): boolean =>
  value.side === 'local' || value.serverId !== undefined

const missingServer = {
  error: 'Für die Serverseite fehlt der Server.',
  path: ['serverId'],
}

const plainName = z
  .string()
  .trim()
  .min(1, 'Der Name darf nicht leer sein.')
  .refine((name) => !name.includes('/') && name !== '.' && name !== '..', {
    error: 'Der Name darf keinen Schrägstrich enthalten.',
  })

export const listDirectoryQuerySchema = z
  .object({
    ...sideTarget,
    /** Empty means the default: the home directory on either side. */
    path: z.string().default(''),
  })
  .refine(remoteWithoutServer, missingServer)

export const createDirectorySchema = z
  .object({
    ...sideTarget,
    /** Directory the new folder goes into. */
    parentPath: z.string().min(1),
    name: plainName,
  })
  .refine(remoteWithoutServer, missingServer)

export const renameSchema = z
  .object({
    ...sideTarget,
    path: z.string().min(1),
    newName: plainName,
  })
  .refine(remoteWithoutServer, missingServer)

export const deletePathsSchema = z
  .object({
    ...sideTarget,
    paths: z.array(z.string().min(1)).min(1, 'Es wurde nichts zum Löschen ausgewählt.'),
  })
  .refine(remoteWithoutServer, missingServer)

export const chmodSchema = z
  .object({
    ...sideTarget,
    path: z.string().min(1),
    /** Permission bits as an octal number, e.g. 0o644 = 420. */
    mode: z.number().int().min(0).max(0o7777),
    recursive: z.boolean().default(false),
  })
  .refine(remoteWithoutServer, missingServer)

/** Only the remote side: the local pane belongs to whoever runs the backend. */
export const chownSchema = z.object({
  serverId: z.string().min(1),
  path: z.string().min(1),
  /** `user`, `user:group` or `:group`, as `chown` takes it. */
  owner: z
    .string()
    .trim()
    .min(1, 'Es wurde kein Besitzer angegeben.')
    .regex(/^[A-Za-z0-9._-]*(:[A-Za-z0-9._-]+)?$/, 'Erlaubt sind Name, Name:Gruppe oder :Gruppe.')
    .refine((owner) => owner !== ':', { error: 'Es wurde kein Besitzer angegeben.' }),
  recursive: z.boolean().default(false),
  /** Answer for `sudo_mode = 'ask'`, kept only for this request. */
  sudoPassword: z.string().optional(),
})

/** Largest file the inline editor opens; beyond this it warns instead (§5.7). */
export const EDITOR_SIZE_LIMIT = 5 * 1024 * 1024

export const fileContentSchema = z.object({
  path: z.string(),
  content: z.string(),
  /** True when the file had to be read with elevated rights. */
  readPrivileged: z.boolean(),
  size: z.number().nonnegative(),
  modifiedAt: z.iso.datetime().nullable(),
})
export type FileContent = z.infer<typeof fileContentSchema>

export const readFileQuerySchema = z.object({
  serverId: z.string().min(1),
  path: z.string().min(1),
  sudoPassword: z.string().optional(),
})

export const writeFileSchema = z.object({
  serverId: z.string().min(1),
  path: z.string().min(1),
  content: z.string(),
  /** Answer for `sudo_mode = 'ask'`, kept only for this request. */
  sudoPassword: z.string().optional(),
})

export const writeFileResultSchema = z.object({
  path: z.string(),
  /** Where the safety copy went (§5.7), or null when the file was new. */
  backupPath: z.string().nullable(),
  writtenPrivileged: z.boolean(),
})
export type WriteFileResult = z.infer<typeof writeFileResultSchema>

/** What a directory action reports back, so the pane can refresh without guessing. */
export const fileActionResultSchema = z.object({
  side: fileSideSchema,
  path: z.string(),
})

export type CreateDirectoryInput = z.input<typeof createDirectorySchema>
export type RenameInput = z.input<typeof renameSchema>
export type DeletePathsInput = z.input<typeof deletePathsSchema>
export type ChmodInput = z.input<typeof chmodSchema>
export type ChownInput = z.input<typeof chownSchema>
export type WriteFileInput = z.input<typeof writeFileSchema>
export type FileActionResult = z.infer<typeof fileActionResultSchema>

/**
 * Eine Sicherungskopie, die der Editor beim Speichern angelegt hat (§5.7).
 *
 * Sie lagen bisher nur neben der Datei herum. Der Editor bietet sie jetzt an: ansehen, mit dem
 * jetzigen Stand vergleichen, zurückspringen.
 */
export const fileVersionSchema = z.object({
  path: z.string(),
  name: z.string(),
  size: z.number().nonnegative(),
  /** Aus dem Zeitstempel im Namen, sonst aus der mtime; null, wenn beides nichts hergab. */
  savedAt: z.iso.datetime().nullable(),
})
export type FileVersion = z.infer<typeof fileVersionSchema>

export const fileVersionsSchema = z.object({
  path: z.string(),
  versions: z.array(fileVersionSchema),
})
export type FileVersions = z.infer<typeof fileVersionsSchema>

export const listVersionsQuerySchema = z.object({
  serverId: z.string().min(1),
  path: z.string().min(1),
})
